const TOMTOM_API_KEY = "PHicnQXMe5eSNFfYZKSVFK1mPbTppl3E";
const LOCATION_UPDATE_INTERVAL = 10000; // 10 seconds
const MAX_RETRIES = 3;
const HIGH_ACCURACY_TIMEOUT = 15000;
const FALLBACK_TIMEOUT = 10000;
const MAX_CACHE_AGE = 5000;

export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString(),
        };
        resolve(location);
      },
      (error) => {

        let errorMessage = "Unable to retrieve location";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "User denied the request for Geolocation";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable";
            break;
          case error.TIMEOUT:
            errorMessage = "The request to get user location timed out";
            break;
          default:
            errorMessage = "An unknown error occurred";
        }

        reject(new Error(errorMessage));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });
};

export const saveLocationToStorage = (location) => {
  try {
    localStorage.setItem("user_location", JSON.stringify(location));
  } catch (error) {
    // Failed to save location
  }
};

export const getLocationFromStorage = () => {
  try {
    const storedLocation = localStorage.getItem("user_location");
    if (storedLocation) {
      const parsed = JSON.parse(storedLocation);
      return parsed;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const clearLocationFromStorage = () => {
  try {
    localStorage.removeItem("user_location");
  } catch (error) {
    // Failed to clear location
  }
};

export const checkStoredLocation = () => {
  const location = getLocationFromStorage();
  return location;
};

// Make it available globally for easy console access
if (typeof window !== "undefined") {
  window.checkUserLocation = checkStoredLocation;
}

export const requestAndSaveLocation = async () => {
  try {
    const location = await getCurrentLocation();
    saveLocationToStorage(location);
    return location;
  } catch (error) {
    throw error;
  }
};

export const checkLocationPermission = async () => {
  if (!navigator.permissions) {
    return false;
  }

  try {
    const permission = await navigator.permissions.query({
      name: "geolocation",
    });
    return permission.state === "granted";
  } catch (error) {
    return false;
  }
};

export const watchUserLocation = (onSuccess, onError) => {
  if (!navigator.geolocation) {
    const error = {
      code: 0,
      message: "Geolocation is not supported by this browser",
    };
    onError?.(error);
    return null;
  }

  let isActive = true;
  let lastPosition = null;
  let retryCount = 0;
  let intervalId = null;

  /**
   * Get current position with retry logic
   */
  const getLocation = () => {
    if (!isActive) return;

    const successCallback = (position) => {
      if (!isActive) return;

      retryCount = 0;
      const { latitude, longitude, accuracy } = position.coords;

      // Only trigger callback if location has changed significantly (> 10 meters)
      const hasChanged =
        !lastPosition ||
        getDistanceInMeters(
          lastPosition.latitude,
          lastPosition.longitude,
          latitude,
          longitude
        ) > 10;

      if (hasChanged) {
        const locationData = { latitude, longitude, accuracy };
        lastPosition = locationData;

        saveLocationToStorage(locationData);
        onSuccess(locationData);
      }
    };

    const errorCallback = (error) => {
      if (!isActive) return;

      onError?.(error);

      if (error.code === 3 && retryCount < MAX_RETRIES) {
        retryCount++;

        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (!isActive) return;
            retryCount = 0;
            const { latitude, longitude, accuracy } = pos.coords;
            const locationData = { latitude, longitude, accuracy };
            lastPosition = locationData;
            saveLocationToStorage(locationData);
            onSuccess(locationData);
          },
          (fallbackError) => {
            if (!isActive) return;
            onError?.(fallbackError);
          },
          {
            enableHighAccuracy: false,
            timeout: FALLBACK_TIMEOUT,
            maximumAge: MAX_CACHE_AGE,
          }
        );
      }
    };

    // Request location with high accuracy
    navigator.geolocation.getCurrentPosition(successCallback, errorCallback, {
      enableHighAccuracy: true,
      timeout: HIGH_ACCURACY_TIMEOUT,
      maximumAge: MAX_CACHE_AGE,
    });
  };

  // Initial location fetch
  getLocation();

  // Set up polling every 20 seconds
  intervalId = setInterval(getLocation, LOCATION_UPDATE_INTERVAL);

  return () => {
    isActive = false;
    if (intervalId) {
      clearInterval(intervalId);
    }
    lastPosition = null;
  };
};

export const getDistanceInMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // Earth's radius in meters
  const toRad = (deg) => (deg * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // distance in meters
};

export const formatDistance = (meters) => {
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  return `${(meters / 1000).toFixed(1)} km`;
};

export const getAddressFromCoordinates = async (latitude, longitude) => {
  const url = `https://api.tomtom.com/search/2/reverseGeocode/${latitude},${longitude}.json?key=${TOMTOM_API_KEY}`;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`TomTom API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data || !data.addresses || data.addresses.length === 0) {
      throw new Error("No address found for these coordinates");
    }

    const result = data.addresses[0];
    const address = {
      fullAddress: result.address?.freeformAddress || "",
      street: result.address?.streetName || "",
      city: result.address?.municipality || "",
      state: result.address?.countrySubdivision || "",
      country: result.address?.country || "",
      postalCode: result.address?.postalCode || "",
      coordinates: { latitude, longitude },
    };

    return address;
  } catch (error) {
    throw error;
  }
};

export const getLocationErrorMessage = (error) => {
  switch (error.code) {
    case 1:
      return "Location access denied. Please enable location permissions in your browser settings.";
    case 2:
      return "Location unavailable. Please check your internet connection and try again.";
    case 3:
      return "Location request timed out. Please try again.";
    default:
      return "Unable to retrieve your location. Please try again.";
  }
};

export default {
  getCurrentLocation,
  saveLocationToStorage,
  getLocationFromStorage,
  clearLocationFromStorage,
  requestAndSaveLocation,
  checkLocationPermission,
  watchUserLocation,
  getDistanceInMeters,
  formatDistance,
  getAddressFromCoordinates,
  getLocationErrorMessage,
};
